// src/widgets/CourseUsersModal.jsx
import { useEffect, useMemo, useState, useCallback } from "react"
import { X, Trash2, LoaderCircle, Plus, Search, Shield, Crown, Phone } from "lucide-react"
import Select from "react-select"
import { AnimatePresence, motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import DataTable from "@/widgets/DataTable"
import { getCourseUsers, mapCourseUsers, unmapCourseUsers, getAdminUsers } from "@/api/kuizy"

const toRoleArray = (roles) => {
  if (Array.isArray(roles)) return roles.filter(Boolean).map(String)
  if (roles == null) return []
  return String(roles).split(",").map(s => s.trim()).filter(Boolean)
}
const phoneDisp = u => ((u.phone_code || "") + (u.phone || "") || "-")
const nameWithDisplay = u => {
  const fn = (u.first_name || "").trim()
  const ln = (u.last_name || "").trim()
  const dn = (u.display_name || "").trim()
  const base = (fn || ln) ? `${fn} ${ln}`.trim() : (u.full_name || u.username || "-")
  return dn ? `${base} (${dn})` : base
}

export default function CourseUsersModal({ courseId, courseName, onClose }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [picker, setPicker] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: "" })

  // คุมปุ่มลบหลายรายการ (ให้เหมือน ClassMembersModal)
  const [selectedMap, setSelectedMap] = useState({})
  const [bulkConfirm, setBulkConfirm] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [roleF, setRoleF] = useState({ value: "", label: "ทุกบทบาท" })

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:courseUsers:perPage")) || 10)
  const [sortBy, setSortBy] = useState("created_at")
  const [sortDir, setSortDir] = useState("desc")
  const [delItem, setDelItem] = useState(null)
  const [deleting, setDeleting] = useState(false)


  useEffect(() => { localStorage.setItem("dt:courseUsers:perPage", String(perPage)) }, [perPage])

  const roleOptions = [
    { value: "", label: "ทุกบทบาท" },
    { value: "MASTER_ADMIN", label: "MASTER_ADMIN" },
    { value: "ADMIN", label: "ADMIN" },
    { value: "CREATOR", label: "CREATOR" },
  ]

  const load = useCallback(() => {
    setLoading(true)
    getCourseUsers(courseId)
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : r.data?.data ?? []
        setRows(arr.map(u => ({ ...u, roles_array: toRoleArray(u.roles) })))
        setSelectedMap({})
        setPage(1)
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [courseId])
  useEffect(load, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(u => {
      const rolesArr = u.roles_array ?? []
      const hay = [
        nameWithDisplay(u),
        u.username,
        u.email,
        phoneDisp(u),
        u.job_title,
        rolesArr.join(", ")
      ].filter(Boolean).join(" ").toLowerCase()
      const passText = !q || hay.includes(q)
      const passRole = !roleF.value || rolesArr.includes(roleF.value)
      return passText && passRole
    })
  }, [rows, search, roleF])

  const displayRows = useMemo(() => {
    const dup = [...filtered]
    const getVal = (r, key) => {
      if (key === "full_name") return nameWithDisplay(r)
      if (key === "role") return (r.roles_array || []).join(",")
      return r[key]
    }
    dup.sort((a, b) => {
      const va = getVal(a, sortBy) ?? ""
      const vb = getVal(b, sortBy) ?? ""
      if (va < vb) return sortDir === "asc" ? -1 : 1
      if (va > vb) return sortDir === "asc" ? 1 : -1
      return 0
    })
    const start = (page - 1) * perPage
    return dup.slice(start, start + perPage)
  }, [filtered, sortBy, sortDir, page, perPage])

  const total = filtered.length

  // DataTable selection
  const rowKey = (u) => `user_${u.user_id}`
  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])
  const onToggleRow = (key) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) delete n[key]
      else n[key] = true
      return n
    })
  }
  const onToggleAll = (keysOnPage) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
  }

  // ลบหลายรายการ — UI/flow แบบเดียวกับ ClassMembersModal
  const doDeleteSelected = async () => {
    const ids = Array.from(selectedSet).map(k => k.split("_")[1])
    if (!ids.length) { setBulkConfirm(false); return }
    setBulkDeleting(true)
    try {
      await unmapCourseUsers(courseId, ids)
      setSelectedMap({})
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      setBulkDeleting(false)
      setBulkConfirm(false)
    }
  }

  const columns = useMemo(() => [
    {
      key: "full_name",
      title: "ผู้ใช้งานระบบ",
      sortable: true,
      sortKey: "full_name",
      render: (u) => (
        <div className="flex items-center gap-3">
          {u.avatar_url ? <img src={u.avatar_url} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-gray-200 rounded-full" />}
          <div className="leading-tight">
            <div className="font-semibold">{nameWithDisplay(u)}</div>
            <div className="text-xs text-gray-500">@{u.username}</div>
          </div>
        </div>
      )
    },
    { key: "job_title", title: "ตำแหน่ง", sortable: true, render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true, headerClassName: "hidden sm:table-cell", cellClassName: "hidden sm:table-cell" },
    { key: "phone", title: "โทรศัพท์", render: (u) => phoneDisp(u), headerClassName: "hidden sm:table-cell", cellClassName: "hidden sm:table-cell" },
    {
      key: "role",
      title: "บทบาท",
      sortable: true,
      sortKey: "role",
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          {(u.roles_array || []).map(r => (
            <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{r}</span>
          ))}
        </div>
      )
    },

    {
      key: "__actions",
      title: "จัดการ",
      align: "right",
      width: 80,
      pinEnd: true,
      render: (u) => (
        <button
          onClick={() => setDelItem(u)}
          className="text-red-600 hover:text-red-800"
          title="ลบ"
        >
          <Trash2 size={16} />
        </button>
      )
    }

  ], [])

  const confirmDeleteOne = async () => {
    if (!delItem) return
    setDeleting(true)
    try {
      await unmapCourseUsers(courseId, [delItem.user_id])
      setDelItem(null)
      load()
    } catch {
      setAlert({ open: true, msg: "ไม่สามารถลบได้" })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AnimatePresence>
      <motion.div key="cuBack" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4">
        <motion.div key="cuCard" initial={{ y: 32, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 32, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[95vh] sm:w-[95%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
          <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
            <h2 className="flex items-center gap-2 text-sm sm:text-base font-semibold">
              <Crown size={18} /> ผู้สอนของ “{courseName}”
            </h2>
            <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
          </header>

          {/* แถบปุ่ม: ปุ่มลบที่เลือกเหมือน ClassMembersModal */}
          <div className="px-4 py-2 border-b border-gray-200 flex items-center gap-2">
            <button onClick={() => setPicker(true)} className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white text-sm rounded-lg px-3 py-1">
              <Plus size={14} /> เพิ่มผู้สอน
            </button>
            <div className="flex-1" />
            <button
              disabled={selectedSet.size === 0}
              onClick={() => setBulkConfirm(true)}
              className="inline-flex items-center gap-1 text-sm rounded-lg px-3 py-1 border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 size={14} /> ลบที่เลือก ({selectedSet.size})
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-4 px-4 py-2 bg-info border-b border-gray-200">
            <div className="flex flex-col gap-1 w-48">
              <label className="flex items-center gap-1 text-sm"><Shield size={16} /> บทบาท</label>
              <Select className="rs-select" classNamePrefix="rs" options={roleOptions} value={roleF} onChange={o => { setRoleF(o || roleOptions[0]); setPage(1) }} isSearchable={false} />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
              <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <div className="flex items-center gap-2">
                  <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && (setSearch(searchIn), setPage(1))} placeholder="ค้นหา ชื่อ–นามสกุล / ชื่อผู้ใช้งานระบบ / อีเมล / เบอร์ / ตำแหน่ง " className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                  <button onClick={() => { setSearch(searchIn); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"><Search size={16} /> ค้นหา</button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 p-3">
            <DataTable
              className="h-full min-h-0"
              columns={columns}
              rows={displayRows}
              loading={loading}
              total={total}
              page={page}
              perPage={perPage}
              sortBy={sortBy}
              sortDir={sortDir}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
              onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
              selectable
              rowKey={rowKey}
              selectedSet={selectedSet}
              onToggleRow={onToggleRow}
              onToggleAll={(keysOnPage) => onToggleAll(keysOnPage)}
            />
          </div>

          <AnimatePresence>
            {picker && (
              <UserPicker
                courseId={courseId}
                mappedIds={new Set(rows.map(u => u.user_id))}
                onSuccess={() => { setPicker(false); load() }}
                onClose={() => setPicker(false)}
              />
            )}
          </AnimatePresence>

          {bulkConfirm && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบหลายรายการ"
              message={`ลบผู้สอนที่เลือกจำนวน ${selectedSet.size} รายการ ?`}
              pending={bulkDeleting}
              onConfirm={doDeleteSelected}
              onCancel={() => setBulkConfirm(false)}
            />
          )}

          {delItem && (
            <ConfirmModal
              type="confirm"
              title="ยืนยันการลบ"
              message={`ลบ “${nameWithDisplay(delItem)}” ออกจากผู้สอนของหลักสูตรนี้ ?`}
              pending={deleting}
              onConfirm={confirmDeleteOne}
              onCancel={() => setDelItem(null)}
            />
          )}

          {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function UserPicker({ courseId, mappedIds, onSuccess, onClose }) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")
  const [role, setRole] = useState({ value: "", label: "ทุกบทบาท" })
  const [selectedMap, setSelectedMap] = useState({})
  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: "" })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:courseUsersPicker:perPage")) || 10)
  const [sortBy, setSortBy] = useState("created_at")
  const [sortDir, setSortDir] = useState("desc")

  useEffect(() => { localStorage.setItem("dt:courseUsersPicker:perPage", String(perPage)) }, [perPage])

  const roleOptions = [
    { value: "", label: "ทุกบทบาท" },
    { value: "MASTER_ADMIN", label: "MASTER_ADMIN" },
    { value: "ADMIN", label: "ADMIN" },
    { value: "CREATOR", label: "CREATOR" },
  ]

  const load = useCallback(() => {
    setLoading(true)
    const excludeIds = Array.from(mappedIds || [])
    getAdminUsers({
      search: search || undefined,
      role: role.value || undefined,
      status: "1",
      page, per_page: perPage,
      sort_by: sortBy, sort_dir: sortDir,
      exclude_course_id: courseId,
      exclude_ids: excludeIds
    })
      .then(r => {
        const d = r?.data || {}
        const arr = Array.isArray(d?.rows) ? d.rows : Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []
        const withRoles = arr.map(u => ({ ...u, roles_array: toRoleArray(u.roles) }))
        const clientFiltered = withRoles.filter(u => !mappedIds.has(u.id))
        setRows(clientFiltered)
        setTotal(Number(d?.total || clientFiltered.length))
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [search, role, page, perPage, sortBy, sortDir, courseId, mappedIds])
  useEffect(load, [load])

  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])
  const rowKey = u => `user_${u.id}`

  const onToggleRow = (key) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) delete n[key]
      else n[key] = true
      return n
    })
  }
  const onToggleAll = (keysOnPage) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
  }

  const save = async () => {
    const ids = Array.from(selectedSet).map(k => k.split("_")[1])
    if (ids.length === 0) return setAlert({ open: true, msg: "ยังไม่ได้เลือกผู้ใช้งานระบบ" })
    setSaving(true)
    try {
      await mapCourseUsers(courseId, ids)
      onSuccess && onSuccess()
    } catch {
      setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" })
    } finally {
      setSaving(false)
    }
  }

  const columns = useMemo(() => [
    {
      key: "full_name",
      title: "ผู้ใช้งานระบบ",
      sortable: true,
      sortKey: "full_name",
      render: (u) => (
        <div className="flex items-center gap-3">
          {u.avatar_url ? <img src={u.avatar_url} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-gray-200 rounded-full" />}
          <div className="leading-tight">
            <div className="font-semibold">{nameWithDisplay(u)}</div>
            <div className="text-xs text-gray-500">@{u.username}</div>
          </div>
        </div>
      )
    },
    { key: "job_title", title: "ตำแหน่ง", sortable: true, render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true, headerClassName: "hidden sm:table-cell", cellClassName: "hidden sm:table-cell" },
    { key: "phone", title: "โทรศัพท์", render: (u) => phoneDisp(u), headerClassName: "hidden sm:table-cell", cellClassName: "hidden sm:table-cell" },
    {
      key: "roles", title: "บทบาท", sortable: true, sortKey: "role",
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          {(u.roles_array || []).map(r => <span key={r} className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary">{r}</span>)}
        </div>
      )
    },
  ], [])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
      <motion.div initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 28, opacity: 0 }} transition={{ duration: 0.2 }} className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
          <h3 className="font-semibold text-sm sm:text-base">เลือกผู้ใช้งานระบบ</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
        </header>

        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-info border-b border-gray-200">
          <div className="flex flex-col gap-1 w-48">
            <label className="flex items-center gap-1 text-sm"><Shield size={16} /> บทบาท</label>
            <Select className="rs-select" classNamePrefix="rs" options={roleOptions} value={role} onChange={o => { setRole(o || roleOptions[0]); setPage(1) }} isSearchable={false} />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
            <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <div className="flex items-center gap-2">
                <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && (setSearch(searchIn), setPage(1))} placeholder="ค้นหา ชื่อ–นามสกุล / ชื่อผู้ใช้งานระบบ / อีเมล / เบอร์ / ตำแหน่ง" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                <button onClick={() => { setSearch(searchIn); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"><Search size={16} /> ค้นหา</button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3">
          <DataTable
            className="h-full min-h-0"
            columns={columns}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            perPage={perPage}
            sortBy={sortBy}
            sortDir={sortDir}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
            onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
            selectable
            rowKey={rowKey}
            selectedSet={selectedSet}
            onToggleRow={onToggleRow}
            onToggleAll={(keysOnPage) => onToggleAll(keysOnPage)}
          />
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">เลือกแล้ว {selectedSet.size} รายการ</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
            <button onClick={save} disabled={selectedSet.size === 0 || saving} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
              {saving && <LoaderCircle size={16} className="animate-spin" />} เพิ่มผู้สอน
            </button>
          </div>
        </div>

        {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />}
      </motion.div>
    </motion.div>
  )
}
